<script setup>
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import { taipeiDay, taipeiToday } from '@/utils/taipeiTime';
import { Button, Switch, Tag } from 'ant-design-vue';
import { LeftOutlined, RightOutlined, VideoCameraOutlined, ShopOutlined, CalendarOutlined } from '@ant-design/icons-vue';
import {
    formatConsultationTimeRange,
    getConsultationBookingStatusMeta
} from '@/utils/consultationBookingStatus';

const props = defineProps({
    bookings: {
        type: Array,
        default: () => []
    },
    viewMode: {
        type: String,
        default: 'week'
    },
    anchorDate: {
        type: Object,
        required: true
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:anchorDate', 'booking-click']);

const WEEKDAY_HEADERS = ['日', '一', '二', '三', '四', '五', '六'];
/** 預設不顯示已取消；可手動打開 */
const showCancelled = ref(false);

const anchor = computed(() => dayjs(props.anchorDate));

const headerLabel = computed(() => {
    if (props.viewMode === 'day') {
        return anchor.value.format('YYYY年M月D日');
    }
    if (props.viewMode === 'week') {
        const start = anchor.value.startOf('week');
        const end = anchor.value.endOf('week');
        return `${start.format('YYYY/M/D')} – ${end.format('M/D')}`;
    }
    return anchor.value.format('YYYY年M月');
});

const visibleBookings = computed(() => {
    if (showCancelled.value) return props.bookings;
    return props.bookings.filter((booking) => booking.status !== 'cancelled');
});

const cancelledCount = computed(
    () => props.bookings.filter((booking) => booking.status === 'cancelled').length
);

const bookingsByDate = computed(() => {
    const map = {};
    for (const booking of visibleBookings.value) {
        if (!booking.starts_at) continue;
        const date = taipeiDay(booking.starts_at).format('YYYY-MM-DD');
        if (!map[date]) map[date] = [];
        map[date].push(booking);
    }
    Object.values(map).forEach((rows) =>
        rows.sort((a, b) => taipeiDay(a.starts_at).valueOf() - taipeiDay(b.starts_at).valueOf())
    );
    return map;
});

const weekDays = computed(() => {
    const start = anchor.value.startOf('week');
    return Array.from({ length: 7 }, (_, i) => {
        const day = start.add(i, 'day');
        const date = day.format('YYYY-MM-DD');
        return {
            date,
            label: day.format('M/D'),
            weekday: WEEKDAY_HEADERS[day.day()],
            isToday: date === taipeiToday(),
            bookings: bookingsByDate.value[date] || []
        };
    });
});

const monthCells = computed(() => {
    const start = anchor.value.startOf('month');
    const daysInMonth = start.daysInMonth();
    const leading = start.day();
    const cells = [];

    for (let i = 0; i < leading; i++) {
        cells.push({ empty: true, key: `pad-${i}` });
    }

    for (let d = 1; d <= daysInMonth; d++) {
        const day = start.date(d);
        const date = day.format('YYYY-MM-DD');
        const dayBookings = bookingsByDate.value[date] || [];
        cells.push({
            empty: false,
            key: date,
            date,
            day: d,
            isToday: date === taipeiToday(),
            count: dayBookings.length,
            bookings: dayBookings
        });
    }

    return cells;
});

const dayBookings = computed(() => {
    const date = anchor.value.format('YYYY-MM-DD');
    return bookingsByDate.value[date] || [];
});

const shift = (unit, amount) => {
    emit('update:anchorDate', anchor.value.add(amount, unit));
};

const goToday = () => {
    emit('update:anchorDate', dayjs());
};

const onBookingClick = (booking) => {
    emit('booking-click', booking);
};

const statusColor = (status) => getConsultationBookingStatusMeta(status).color;
const statusLabel = (status) => getConsultationBookingStatusMeta(status).label;
</script>

<template>
    <div class="booking-calendar" :class="{ 'is-loading': loading }">
        <div class="booking-calendar__nav">
            <div class="booking-calendar__nav-period">{{ headerLabel }}</div>
            <div class="booking-calendar__nav-actions">
                <label class="booking-calendar__filter">
                    <Switch v-model:checked="showCancelled" size="small" />
                    <span>顯示已取消{{ cancelledCount ? `（${cancelledCount}）` : '' }}</span>
                </label>
                <Button class="booking-calendar__nav-btn" @click="goToday">今天</Button>
                <Button
                    class="booking-calendar__nav-btn booking-calendar__nav-btn--icon"
                    aria-label="上一段"
                    @click="shift(viewMode === 'month' ? 'month' : viewMode === 'week' ? 'week' : 'day', -1)"
                >
                    <LeftOutlined />
                </Button>
                <Button
                    class="booking-calendar__nav-btn booking-calendar__nav-btn--icon"
                    aria-label="下一段"
                    @click="shift(viewMode === 'month' ? 'month' : viewMode === 'week' ? 'week' : 'day', 1)"
                >
                    <RightOutlined />
                </Button>
            </div>
        </div>

        <div v-if="viewMode === 'week'" class="booking-calendar__week">
            <div
                v-for="col in weekDays"
                :key="col.date"
                class="booking-calendar__week-col"
                :class="{ 'is-today': col.isToday }"
            >
                <div class="booking-calendar__week-head">
                    <span class="booking-calendar__weekday">{{ col.weekday }}</span>
                    <span class="booking-calendar__weekdate">{{ col.label }}</span>
                </div>
                <div class="booking-calendar__week-body">
                    <button
                        v-for="booking in col.bookings"
                        :key="booking.id"
                        type="button"
                        class="booking-calendar__event"
                        @click="onBookingClick(booking)"
                    >
                        <div class="booking-calendar__event-head">
                            <span class="booking-calendar__event-time">
                                {{ taipeiDay(booking.starts_at).format('HH:mm') }}
                            </span>
                            <Tag
                                :color="statusColor(booking.status)"
                                class="booking-calendar__event-tag"
                            >
                                {{ statusLabel(booking.status) }}
                            </Tag>
                            <span class="booking-calendar__event-mode">
                                <VideoCameraOutlined v-if="booking.mode !== 'offline'" />
                                <ShopOutlined v-else />
                            </span>
                        </div>
                        <span class="booking-calendar__event-name">
                            {{ booking.user_name }}
                        </span>
                        <span
                            v-if="booking.status === 'confirmed'"
                            class="booking-calendar__event-cta"
                        >
                            進入場次
                        </span>
                    </button>
                    <div v-if="!col.bookings.length" class="booking-calendar__empty">
                        <span class="booking-calendar__empty-dot" />
                        無預約
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="viewMode === 'day'" class="booking-calendar__day">
            <div v-if="dayBookings.length" class="booking-calendar__day-list">
                <button
                    v-for="booking in dayBookings"
                    :key="booking.id"
                    type="button"
                    class="booking-calendar__day-item"
                    @click="onBookingClick(booking)"
                >
                    <div class="booking-calendar__day-time">
                        {{ formatConsultationTimeRange(booking.starts_at, booking.ends_at) }}
                    </div>
                    <div class="booking-calendar__day-main">
                        <span class="booking-calendar__day-name">{{ booking.user_name }}</span>
                        <span class="booking-calendar__day-meta">
                            {{ booking.mode === 'offline' ? '實體' : '線上' }}
                            <template v-if="booking.version_name"> · {{ booking.version_name }}</template>
                        </span>
                    </div>
                    <Tag :color="statusColor(booking.status)">{{ statusLabel(booking.status) }}</Tag>
                    <span v-if="booking.status === 'confirmed'" class="booking-calendar__day-cta">
                        進入場次
                    </span>
                </button>
            </div>
            <div v-else class="booking-calendar__empty-day">
                <div class="booking-calendar__empty-illust" aria-hidden="true">
                    <CalendarOutlined />
                </div>
                <p class="booking-calendar__empty-title">本日尚無預約</p>
                <p class="booking-calendar__empty-hint">可切換週或月檢視其他時段</p>
            </div>
        </div>

        <div v-else class="booking-calendar__month">
            <div class="booking-calendar__month-head">
                <span v-for="h in WEEKDAY_HEADERS" :key="h">{{ h }}</span>
            </div>
            <div class="booking-calendar__month-grid">
                <div
                    v-for="cell in monthCells"
                    :key="cell.key"
                    class="booking-calendar__month-cell"
                    :class="{
                        'is-empty': cell.empty,
                        'is-today': !cell.empty && cell.isToday,
                        'has-bookings': !cell.empty && cell.count > 0
                    }"
                >
                    <template v-if="!cell.empty">
                        <button
                            type="button"
                            class="booking-calendar__month-day"
                            @click="emit('update:anchorDate', dayjs(cell.date))"
                        >
                            {{ cell.day }}
                        </button>
                        <div v-if="cell.count" class="booking-calendar__month-count">
                            {{ cell.count }} 筆
                        </div>
                        <button
                            v-for="booking in cell.bookings.slice(0, 2)"
                            :key="booking.id"
                            type="button"
                            class="booking-calendar__month-event"
                            @click="onBookingClick(booking)"
                        >
                            {{ taipeiDay(booking.starts_at).format('HH:mm') }} {{ booking.user_name }}
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.booking-calendar.is-loading {
    opacity: 0.65;
    pointer-events: none;
}

.booking-calendar__nav {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    padding: 10px 14px;
    border-radius: 12px;
    background: linear-gradient(135deg, #f8f4eb 0%, #f8fafc 100%);
    border: 1px solid #e2e8f0;
}

.booking-calendar__nav-period {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
}

.booking-calendar__nav-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.booking-calendar__filter {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-right: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    user-select: none;
}

.booking-calendar__nav-btn {
    height: 32px;
    border-radius: 8px;
    border-color: #cbd5e1;
    font-weight: 600;
    font-size: 13px;
    color: #475569;
}

.booking-calendar__nav-btn--icon {
    width: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.booking-calendar__week {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 10px;
    min-height: 300px;
}

.booking-calendar__week-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid #e8edf2;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
    transition: box-shadow 0.15s ease;
}

.booking-calendar__week-col:hover {
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.booking-calendar__week-col.is-today {
    border-color: #e2e8f0;
    box-shadow: none;
}

.booking-calendar__week-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 4px;
    padding: 10px 12px;
    border-bottom: 1px solid #e8edf2;
    background: #fafbfc;
}

.booking-calendar__week-col.is-today .booking-calendar__week-head {
    background: #fafbfc;
}

.booking-calendar__weekday {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
}

.booking-calendar__weekdate {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
}

.booking-calendar__week-col.is-today .booking-calendar__weekdate {
    color: #8b6914;
}

.booking-calendar__week-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
}

.booking-calendar__event {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
    padding: 10px 10px 9px;
    border: 1px solid #e8edf2;
    border-radius: 10px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        transform 0.15s ease;
}

.booking-calendar__event:hover {
    border-color: #d6d0c4;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(43, 43, 43, 0.08);
}

.booking-calendar__event-head {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
}

.booking-calendar__event-time {
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 700;
    color: #8b6914;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
}

.booking-calendar__event-name {
    width: 100%;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.booking-calendar__event-tag {
    flex: 0 0 auto;
    margin: 0;
    white-space: nowrap;
    font-size: 10px;
    line-height: 18px;
    border-radius: 6px;
}

.booking-calendar__event-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 4px;
    padding: 4px 10px;
    border-radius: 8px;
    border: 1px solid #8b6914;
    background: linear-gradient(180deg, #d4b56a 0%, #8b6914 100%);
    color: #fffdf8;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.3;
    box-shadow: 0 2px 8px rgba(139, 105, 20, 0.2);
}

.booking-calendar__event:hover .booking-calendar__event-cta {
    background: linear-gradient(180deg, #c4a35a 0%, #6b5428 100%);
}

.booking-calendar__event-mode {
    margin-left: auto;
    flex: 0 0 auto;
    color: #64748b;
    font-size: 12px;
    line-height: 1;
}

.booking-calendar__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    min-height: 72px;
    font-size: 12px;
    color: #cbd5e1;
}

.booking-calendar__empty-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #e2e8f0;
}

.booking-calendar__day-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.booking-calendar__day-item {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 16px;
    border: 1px solid #e8edf2;
    border-radius: 12px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.booking-calendar__day-item:hover {
    border-color: #d6d0c4;
    box-shadow: 0 4px 14px rgba(43, 43, 43, 0.08);
}

.booking-calendar__day-time {
    min-width: 110px;
    font-size: 13px;
    font-weight: 700;
    color: #8b6914;
    font-variant-numeric: tabular-nums;
}

.booking-calendar__day-main {
    flex: 1;
    min-width: 0;
}

.booking-calendar__day-name {
    display: block;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}

.booking-calendar__day-meta {
    display: block;
    margin-top: 3px;
    font-size: 12px;
    color: #64748b;
}

.booking-calendar__day-cta {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid #8b6914;
    background: linear-gradient(180deg, #d4b56a 0%, #8b6914 100%);
    color: #fffdf8;
    font-size: 12px;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(139, 105, 20, 0.2);
}

.booking-calendar__empty-day {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 200px;
    padding: 32px 24px;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    text-align: center;
}

.booking-calendar__empty-illust {
    display: grid;
    place-items: center;
    width: 52px;
    height: 52px;
    margin-bottom: 4px;
    border-radius: 14px;
    background: #f6f1e6;
    border: 1px solid #ede4d0;
    color: #8b6914;
    font-size: 22px;
}

.booking-calendar__empty-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #475569;
}

.booking-calendar__empty-hint {
    margin: 0;
    font-size: 13px;
    color: #94a3b8;
}

.booking-calendar__month-head {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-align: center;
}

.booking-calendar__month-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}

.booking-calendar__month-cell {
    min-height: 92px;
    padding: 8px;
    border: 1px solid #f1f5f9;
    border-radius: 10px;
    background: #fff;
}

.booking-calendar__month-cell.is-empty {
    border-color: transparent;
    background: transparent;
}

.booking-calendar__month-cell.is-today {
    border-color: #8b6914;
    background: #f8f4eb;
}

.booking-calendar__month-cell.has-bookings {
    background: #fafbfc;
}

.booking-calendar__month-day {
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    cursor: pointer;
}

.booking-calendar__month-count {
    margin: 4px 0;
    font-size: 10px;
    color: #8b6914;
    font-weight: 600;
}

.booking-calendar__month-event {
    display: block;
    width: 100%;
    margin-top: 3px;
    padding: 3px 6px;
    border: 0;
    border-radius: 6px;
    background: #f6f1e6;
    font-size: 10px;
    color: #6b5428;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.booking-calendar__month-event:hover {
    background: #ede4d0;
}

@media (max-width: 900px) {
    .booking-calendar__week {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
